/**
 * Canonical workspace surface-entry decisions.
 *
 * "Can enter a surface shell" is a DIFFERENT decision from "can see every
 * resource in that surface". A user may enter the Projects surface either
 * because their account role grants a workspace-wide list capability, OR
 * because they hold at least one active scoped relationship that supports
 * discovery of that surface. In both cases the server-filtered list and the
 * per-resource detail/mutation checks remain authoritative — this helper only
 * governs SHELL ENTRY (navigation visibility + route shell mount).
 *
 * This deliberately avoids the previous inconsistency where the Sidebar used a
 * broad `canViewProjects(caps)` account capability that hid the surface from a
 * relationship-entitled `organization_member`.
 */
import type { User } from '@/types';
import type {
  ScopedRelationshipType,
  UserScopedRelationship,
} from '@/types';
import type { Capability } from './capabilities';
import { getActiveScopedRelationships } from './scopedRelationshipResolver';

export type WorkspaceSurfaceId =
  | 'projects'
  | 'chat'
  | 'milestones'
  | 'tasks'
  | 'assets'
  | 'documents'
  | 'companies'
  | 'reports'
  | 'investments'
  | 'decisions'
  | 'users';

interface SurfaceEntryRule {
  /** Any of these account-role capabilities grants workspace-wide entry. */
  readonly anyCapability: readonly Capability[];
  /** Any active relationship of these types grants scoped entry. */
  readonly relationshipTypes: readonly ScopedRelationshipType[];
}

const SURFACE_ENTRY_RULES: Readonly<Record<WorkspaceSurfaceId, SurfaceEntryRule>> = {
  chat: {
    anyCapability: ['calendar.view', 'projects.view', 'investor.chat.access'],
    relationshipTypes: [
      'project_manager', 'project_contributor',
      'partner_representative', 'vendor_representative', 'company_representative',
      'observer',
    ],
  },
  projects: {
    anyCapability: ['projects.view'],
    relationshipTypes: [
      'project_manager', 'project_contributor',
      'partner_representative', 'vendor_representative', 'company_representative',
      'observer',
    ],
  },
  // Milestones / Capital Gates — governed execution objects on projects.
  // Account-wide projects.view (or project analytics) still grants entry; scoped
  // project managers and similar relationships must too. Validators are
  // object-scoped to gates/milestones and need the shell without project-wide
  // membership.
  milestones: {
    anyCapability: ['projects.view', 'analytics.view.projects'],
    relationshipTypes: [
      'project_manager', 'project_contributor',
      'partner_representative', 'vendor_representative', 'company_representative',
      'observer',
      'validator',
    ],
  },
  tasks: {
    anyCapability: ['tasks.view'],
    relationshipTypes: [
      'project_manager', 'project_contributor',
      'partner_representative', 'vendor_representative',
      'validator',
    ],
  },
  assets: {
    anyCapability: ['files.view'],
    relationshipTypes: [
      'project_manager', 'project_contributor',
      'partner_representative', 'vendor_representative', 'company_representative',
    ],
  },
  documents: {
    anyCapability: ['files.view'],
    relationshipTypes: [
      'project_manager', 'project_contributor',
      'partner_representative', 'vendor_representative', 'company_representative',
    ],
  },
  companies: {
    anyCapability: ['companies.view'],
    relationshipTypes: ['company_representative'],
  },
  reports: {
    anyCapability: ['reports.view'],
    relationshipTypes: ['reporting_recipient'],
  },
  investments: {
    // Investment positions are always person-to-investment scoped. An
    // organisation administrator may hold rights-management capabilities, but
    // until an administrative registry exists this surface has no useful
    // workspace-wide content for them. Do not advertise an empty page or
    // broaden financial-position visibility merely from an account role.
    anyCapability: [],
    relationshipTypes: ['investor_representative', 'investment_manager'],
  },
  decisions: {
    anyCapability: ['gates.view'],
    relationshipTypes: ['decision_participant', 'validator', 'observer'],
  },
  users: {
    anyCapability: ['users.view.global', 'users.view.collaborators'],
    relationshipTypes: ['project_manager', 'partner_representative', 'vendor_representative'],
  },
};

/**
 * Relationship type alone is not enough to advertise a surface. A validator
 * assigned to one Task, for example, must not see an empty Decisions or
 * Milestones destination merely because validators can also be assigned to
 * those object types. Keep the resource-shape check beside the surface rules
 * so Sidebar, route recovery and page adapters cannot drift apart.
 */
function relationshipSupportsSurface(
  surface: WorkspaceSurfaceId,
  relationship: UserScopedRelationship,
): boolean {
  const { type, resourceType } = relationship;

  switch (surface) {
    case 'chat':
      if (type === 'observer') return resourceType === 'project';
      if (type === 'project_manager' || type === 'project_contributor') return resourceType === 'project';
      if (type === 'company_representative' || type === 'partner_representative' || type === 'vendor_representative') {
        return resourceType === 'company' || resourceType === 'project';
      }
      return false;
    case 'projects':
      if (type === 'observer') return resourceType === 'project';
      if (type === 'project_manager' || type === 'project_contributor') return resourceType === 'project';
      if (type === 'company_representative' || type === 'partner_representative' || type === 'vendor_representative') {
        return resourceType === 'company' || resourceType === 'project';
      }
      return false;
    case 'milestones':
      if (type === 'validator') return resourceType === 'gate' || resourceType === 'milestone';
      if (type === 'observer') return resourceType === 'project' || resourceType === 'gate' || resourceType === 'milestone';
      if (type === 'project_manager' || type === 'project_contributor') return resourceType === 'project';
      if (type === 'company_representative' || type === 'partner_representative' || type === 'vendor_representative') {
        return resourceType === 'company' || resourceType === 'project';
      }
      return false;
    case 'tasks':
      if (type === 'validator') return resourceType === 'task';
      if (type === 'project_manager' || type === 'project_contributor') return resourceType === 'project';
      if (type === 'partner_representative' || type === 'vendor_representative') {
        return resourceType === 'company' || resourceType === 'project';
      }
      return false;
    case 'assets':
    case 'documents':
      if (type === 'project_manager' || type === 'project_contributor') return resourceType === 'project';
      if (type === 'company_representative' || type === 'partner_representative' || type === 'vendor_representative') {
        return resourceType === 'company' || resourceType === 'project';
      }
      return false;
    case 'companies':
      return type === 'company_representative' && resourceType === 'company';
    case 'reports':
      return type === 'reporting_recipient';
    case 'investments':
      return resourceType === 'investment'
        && (type === 'investor_representative' || type === 'investment_manager');
    case 'decisions':
      return resourceType === 'decision'
        && (type === 'decision_participant' || type === 'validator' || type === 'observer');
    case 'users':
      if (type === 'project_manager') return resourceType === 'project';
      if (type === 'partner_representative' || type === 'vendor_representative') {
        return resourceType === 'project';
      }
      return false;
  }
}

const FOCUSED_ASSIGNMENT_RELATIONSHIPS: ReadonlySet<ScopedRelationshipType> = new Set([
  'reporting_recipient',
  'decision_participant',
  'validator',
  'observer',
]);

/**
 * True for people invited to a narrow assignment rather than an operating
 * workspace experience. Baseline workspace membership is ignored here because
 * it establishes the tenant boundary, not a product destination.
 */
export function isFocusedAssignmentParticipant(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
): boolean {
  const assignments = getActiveScopedRelationships(user)
    .filter((relationship) => relationship.type !== 'workspace_member');
  return assignments.length > 0
    && assignments.every((relationship) => FOCUSED_ASSIGNMENT_RELATIONSHIPS.has(relationship.type));
}

/** The first useful destination for a narrow assignment persona. */
export function resolveFocusedAssignmentSurface(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
  capabilities: readonly Capability[],
): WorkspaceSurfaceId | null {
  const priority: readonly WorkspaceSurfaceId[] = [
    'reports', 'decisions', 'tasks', 'milestones', 'projects',
  ];
  return priority.find((surface) => canEnterWorkspaceSurface(surface, user, capabilities)) ?? null;
}

/**
 * Whether the user may ENTER the given workspace surface shell. Entry is
 * granted by a workspace-wide account capability OR at least one active scoped
 * relationship supporting discovery of that surface. Fails closed for unknown
 * surfaces and for users with neither capability nor supporting relationship.
 */
export function canEnterWorkspaceSurface(
  surface: WorkspaceSurfaceId,
  user: Pick<User, 'scopedRelationships'> | null | undefined,
  capabilities: readonly Capability[],
): boolean {
  const rule = SURFACE_ENTRY_RULES[surface];
  if (!rule) return false;
  if (rule.anyCapability.some((capability) => capabilities.includes(capability))) {
    return true;
  }
  const relationships = getActiveScopedRelationships(user);
  return relationships.some((relationship) => (
    rule.relationshipTypes.includes(relationship.type)
    && relationshipSupportsSurface(surface, relationship)
  ));
}
